import React from "react";
import { Stack, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { validateLength, validateUrl } from "../../utils/validation.util";
import { RpiBodyText } from "@rpi/openapi-core";
import { countrySelectData } from "../../utils/country.util";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";
import { RpaButtonPromo } from "../../components/buttons/RpaButton";
import { GraphQL } from "@rpi/openapi-api";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";

const sizes = ["Startup (1-10)", "Small Company (11-50)", "Mid-Sized Company (51-200)", "Large Company (201+)"];

export const OnBoarding: React.FC = () => {
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const navigate = useNavigateRoute();
  const location = useLocation();
  const [countrySearchValue, setCountrySearchValue] = React.useState("");

  const form = useForm<GraphQL.OnboardingInput>({
    initialValues: {
      company: "",
      size: undefined,
      website: undefined,
      countryIso: undefined,
    },
    validate: {
      company: validateLength("Company", { min: 3 }),
      website: (value?: string) => (value ? validateUrl(value) : null),
    },
  });

  const onboardCustomer = GraphQL.useOnboardCustomerMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["DashboardUserCurrent"] });
      navigate(["setup-payment"], { state: { from: location } });
    },
  });

  // because we invalidate the dashboard user, waiting for that to finish fetching is also a loading state
  const isLoading = onboardCustomer.isLoading || user.isDashboardUserFetching;

  const handleSubmit = (values: GraphQL.OnboardingInput) => {
    onboardCustomer.mutate({ input: values });
  };

  return (
    <Stack
      sx={(theme) => ({
        gap: theme.spacing.lg,
      })}
    >
      <Title
        style={{
          fontFamily: "Neue Haas Grotesk Display Pro, sans-serif",
          fontSize: "24px",
          fontWeight: 600,
        }}
      >
        Hi {user.dashboardUser?.firstName || "There"} 👋
      </Title>
      <RpiBodyText>Tell us a bit about your organization to get started.</RpiBodyText>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack
          sx={(theme) => ({
            gap: theme.spacing.lg,
          })}
        >
          <Stack>
            <RpaTextInput
              label="Organization"
              placeholder="Enter"
              required={true}
              disabled={isLoading}
              data-testid="organization"
              {...form.getInputProps("company")}
            />
            <RpaDropdown
              label="Size"
              data={sizes}
              placeholder="Select.."
              optional={true}
              disabled={isLoading}
              {...form.getInputProps("size")}
            />
            <RpaTextInput label="Website" placeholder="www." optional={true} {...form.getInputProps("website")} />
            <RpaDropdown
              label="Country"
              data={countrySelectData}
              placeholder="Select.."
              optional={true}
              disabled={isLoading}
              filterDataOnExactSearchMatch
              searchable
              searchValue={countrySearchValue}
              onSearchChange={setCountrySearchValue}
              nothingFound="No countries found"
              {...form.getInputProps("countryIso")}
            />
          </Stack>
          <RpaButtonPromo
            sx={{ height: "40px" }}
            fullWidth={true}
            data-testid={"continueButton"}
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Continue"}
          </RpaButtonPromo>
        </Stack>
      </form>
    </Stack>
  );
};
